import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { apiClient, getErrorMessage } from '../../api/client'
import { useAuthStore } from '../../stores/authStore'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'

const schema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  date_of_birth: z.string().min(1, 'DOB is required'),
  gender: z.enum(['male', 'female', 'other', 'unknown']).default('unknown'),
  address_line1: z.string().min(1, 'Address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2).max(2),
  zip_code: z.string().min(5),
  primary_phone: z.string().min(10, 'Phone is required'),
  email: z.string().email().optional().or(z.literal('')),
  medical_record_number: z.string().min(1, 'MRN is required'),
  status: z.enum(['intake', 'active', 'on_hold', 'discharged', 'waitlist']).default('intake'),
  primary_diagnosis_code: z.string().min(1, 'Primary diagnosis code is required'),
  primary_diagnosis_description: z.string().min(1, 'Primary diagnosis description is required'),
  // Diagnosis history
  diagnosis_date: z.string().optional().or(z.literal('')),
  diagnosing_provider_name: z.string().optional().or(z.literal('')),
  diagnosing_provider_credentials: z.string().optional().or(z.literal('')),
  // Living situation
  lives_with: z.string().optional().or(z.literal('')),
  // Medical history
  allergies: z.string().optional().or(z.literal('')),
  current_medications: z.string().optional().or(z.literal('')),
  medical_background: z.string().optional().or(z.literal('')),
  previous_interventions: z.string().optional().or(z.literal('')),
})

type FormData = z.infer<typeof schema>

function normalizePhoneToE164(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return trimmed

  if (trimmed.startsWith('+')) {
    return `+${trimmed.slice(1).replace(/\D/g, '')}`
  }

  const digits = trimmed.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return `+${digits}`
}

export default function ClientCreatePage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isOnline = useOnlineStatus()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const canCreateClient = user?.role === 'admin' || user?.role === 'bcba'
  const defaultValues = useMemo<FormData>(
    () => ({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: 'unknown',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      primary_phone: '',
      email: '',
      medical_record_number: '',
      status: 'intake',
      primary_diagnosis_code: '',
      primary_diagnosis_description: '',
      diagnosis_date: '',
      diagnosing_provider_name: '',
      diagnosing_provider_credentials: '',
      lives_with: '',
      allergies: '',
      current_medications: '',
      medical_background: '',
      previous_interventions: '',
    }),
    []
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    if (!isOnline) {
      setSubmitError('Client creation requires an online connection.')
      return
    }

    try {
      const payload = {
        ...data,
        state: data.state.trim().toUpperCase(),
        primary_phone: normalizePhoneToE164(data.primary_phone),
        email: data.email?.trim() ? data.email.trim() : null,
      }

      const res = await apiClient.post('/api/v1/clients/', payload)
      const clientId = res.data?.id
      if (clientId) {
        navigate(`/app/clients/${clientId}`)
      } else {
        navigate('/app/clients')
      }
    } catch (err) {
      setSubmitError(getErrorMessage(err))
    }
  }

  if (!canCreateClient) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        You do not have permission to create clients.
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Client</h1>
          <p className="text-sm text-gray-600">
            PHI is encrypted at rest. Tenant isolation is enforced on every request.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => navigate('/app/clients')}>
          Cancel
        </button>
      </div>

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="first_name">
              First name
            </label>
            <input id="first_name" className="input mt-1" {...register('first_name')} />
            {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="last_name">
              Last name
            </label>
            <input id="last_name" className="input mt-1" {...register('last_name')} />
            {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="date_of_birth">
              Date of birth
            </label>
            <input id="date_of_birth" type="date" className="input mt-1" {...register('date_of_birth')} />
            {errors.date_of_birth && <p className="mt-1 text-sm text-red-600">{errors.date_of_birth.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="gender">
              Gender
            </label>
            <select id="gender" className="input mt-1" {...register('gender')}>
              <option value="unknown">Unknown</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="label" htmlFor="address_line1">
              Address line 1
            </label>
            <input id="address_line1" className="input mt-1" {...register('address_line1')} />
            {errors.address_line1 && <p className="mt-1 text-sm text-red-600">{errors.address_line1.message}</p>}
          </div>

          <div className="md:col-span-2">
            <label className="label" htmlFor="address_line2">
              Address line 2
            </label>
            <input id="address_line2" className="input mt-1" {...register('address_line2')} />
          </div>

          <div>
            <label className="label" htmlFor="city">
              City
            </label>
            <input id="city" className="input mt-1" {...register('city')} />
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="state">
              State (2-letter)
            </label>
            <input id="state" className="input mt-1" maxLength={2} {...register('state')} />
            {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="zip_code">
              ZIP
            </label>
            <input id="zip_code" className="input mt-1" {...register('zip_code')} />
            {errors.zip_code && <p className="mt-1 text-sm text-red-600">{errors.zip_code.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="primary_phone">
              Primary phone
            </label>
            <input id="primary_phone" className="input mt-1" {...register('primary_phone')} />
            {errors.primary_phone && <p className="mt-1 text-sm text-red-600">{errors.primary_phone.message}</p>}
            <p className="mt-1 text-xs text-gray-500">Stored as E.164 (e.g., +15551234567).</p>
          </div>

          <div>
            <label className="label" htmlFor="email">
              Contact email (optional)
            </label>
            <input id="email" type="email" className="input mt-1" {...register('email')} />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
          </div>

          <div>
            <label className="label" htmlFor="medical_record_number">
              MRN
            </label>
            <input id="medical_record_number" className="input mt-1" {...register('medical_record_number')} />
            {errors.medical_record_number && (
              <p className="mt-1 text-sm text-red-600">{errors.medical_record_number.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="status">
              Status
            </label>
            <select id="status" className="input mt-1" {...register('status')}>
              <option value="intake">Intake</option>
              <option value="active">Active</option>
              <option value="on_hold">On hold</option>
              <option value="discharged">Discharged</option>
              <option value="waitlist">Waitlist</option>
            </select>
          </div>

          <div>
            <label className="label" htmlFor="primary_diagnosis_code">
              Primary diagnosis code
            </label>
            <input id="primary_diagnosis_code" className="input mt-1" {...register('primary_diagnosis_code')} />
            {errors.primary_diagnosis_code && (
              <p className="mt-1 text-sm text-red-600">{errors.primary_diagnosis_code.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="label" htmlFor="primary_diagnosis_description">
              Primary diagnosis description
            </label>
            <input
              id="primary_diagnosis_description"
              className="input mt-1"
              {...register('primary_diagnosis_description')}
            />
            {errors.primary_diagnosis_description && (
              <p className="mt-1 text-sm text-red-600">{errors.primary_diagnosis_description.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="diagnosis_date">Date of diagnosis</label>
            <input id="diagnosis_date" type="date" className="input mt-1" {...register('diagnosis_date')} />
          </div>

          <div>
            <label className="label" htmlFor="diagnosing_provider_name">Diagnosing provider</label>
            <input
              id="diagnosing_provider_name"
              className="input mt-1"
              placeholder="e.g. Dr. Jane Smith"
              {...register('diagnosing_provider_name')}
            />
          </div>

          <div>
            <label className="label" htmlFor="diagnosing_provider_credentials">Provider credentials</label>
            <input
              id="diagnosing_provider_credentials"
              className="input mt-1"
              placeholder="e.g. Ph.D., MD, BCBA"
              {...register('diagnosing_provider_credentials')}
            />
          </div>
        </div>

        <details open className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-gray-800 select-none">
            Living situation
          </summary>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label" htmlFor="lives_with">Lives with</label>
              <input
                id="lives_with"
                className="input mt-1"
                placeholder="e.g. Both biological parents and one sibling"
                {...register('lives_with')}
              />
              <p className="mt-1 text-xs text-gray-500">Describe the household. Add guardians separately after the client is created.</p>
            </div>
          </div>
        </details>

        <details open className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-gray-800 select-none">
            Medical history
          </summary>
          <div className="mt-4 grid grid-cols-1 gap-4">
            <div>
              <label className="label" htmlFor="medical_background">Medical / developmental background</label>
              <textarea
                id="medical_background"
                rows={4}
                className="input mt-1"
                placeholder="Birth history, hospitalizations, comorbid conditions, developmental milestones."
                {...register('medical_background')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="allergies">Allergies</label>
                <textarea
                  id="allergies"
                  rows={3}
                  className="input mt-1"
                  placeholder="Medications, foods, environmental. One per line."
                  {...register('allergies')}
                />
              </div>
              <div>
                <label className="label" htmlFor="current_medications">Current medications</label>
                <textarea
                  id="current_medications"
                  rows={3}
                  className="input mt-1"
                  placeholder="Medication, dosage, frequency, prescriber. One per line."
                  {...register('current_medications')}
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="previous_interventions">Previous interventions / services</label>
              <textarea
                id="previous_interventions"
                rows={3}
                className="input mt-1"
                placeholder="Speech therapy, OT, prior ABA, counseling, school accommodations and outcomes."
                {...register('previous_interventions')}
              />
            </div>
          </div>
        </details>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => navigate('/app/clients')}>
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving...' : 'Create client'}
          </button>
        </div>
      </form>
    </div>
  )
}

